/**
 * Name search: the one rule every search box in Teamna uses (Desk web, mobile, API).
 *
 * People type the same child in many ways: Mohammed, Mohamed, mohmed, Mhmd, محمد. The rule finds
 * them all, and keeps names apart that only look alike (Lana and Hana, Salma and Salem). Proposal
 * and demo on real names: strategy repo, artifact "Smart name search" (approved 2026-10-23).
 *
 *   1. Split the name into words. "el gholi" is one word. "elswedi" is also kept as "swedi", and
 *      two words side by side also as one ("abd elghany" is also "abdelghany"). Arabic letters
 *      become Latin letters.
 *   2. Fold each word to a sound key: Layla and Laila, Tariq and Tarik, Kareem and Karim meet.
 *   3. Each typed word must match a different word of the name, in any order. The first way that
 *      works gives the band: same spelling (or a start of the last word while typing), same sound
 *      key, close spelling, or Arabic script on one side with the same consonants.
 *   4. A row can have more texts than its name (a parent, a level): `searchByName` takes the best.
 *
 * `nameWordKey` and `nameKey` in `names.ts` stay as they are: the API stores `nameKey` on each
 * waiting name, so that fold must not change.
 */

/** Why a name matched, best first. */
export const nameBands = ["exact", "sounds", "typo", "arabic"] as const;
export type NameBand = (typeof nameBands)[number];

/** `first`: a typed word matched the first word of the name (the first name). */
export type NameHit = { score: number; band: NameBand; first: boolean };

type Word = {
  /** Lower case, no accents, letters a to z only (Arabic script written in Latin letters). */
  raw: string;
  /** The sound key. */
  key: string;
  /** The consonants of the sound key, the first letter kept. */
  bones: string;
  arabic: boolean;
  /** The place of the word in the name. A joined word covers `pos` and `pos + 1`. */
  pos: number;
  span: 1 | 2;
  /** A second form of a word ("swedi", "abdelghany"): matched, never typed. */
  variant: boolean;
};

export type NameQuery = { words: Word[]; typing: boolean };

const ARABIC = /[؀-ۿ]/;
const ARABIC_LATIN: Record<string, string> = {
  ا: "a", أ: "a", إ: "i", آ: "a", ب: "b", ت: "t", ث: "th", ج: "j", ح: "h", خ: "kh", د: "d", ذ: "z",
  ر: "r", ز: "z", س: "s", ش: "sh", ص: "s", ض: "d", ط: "t", ظ: "z", ع: "", غ: "gh", ف: "f", ق: "k",
  ك: "k", ل: "l", م: "m", ن: "n", ه: "h", ة: "a", و: "w", ي: "y", ى: "a", ئ: "y", ؤ: "w", ء: "",
};
const ARTICLES = new Set(["el", "al"]);

function rawWord(word: string): string {
  let text = word.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  if (ARABIC.test(text)) text = text.replace(/[؀-ۿ]/g, (c) => ARABIC_LATIN[c] ?? "");
  return text.replace(/[^a-z]/g, "");
}

/** The sound key of a word: the spellings people mix up meet. */
export function soundKey(raw: string): string {
  return (
    raw
      // "el" before a consonant is the article: "elswedi" and "alswedi" meet. Any other first "e"
      // is "i" (Eman and Iman, Essa and Issa), so Enas stays apart from Anas.
      .replace(/^el(?=[^aeiou])/, "al")
      .replace(/^e/, "i")
      .replace(/ph/g, "f")
      .replace(/kh/g, "k")
      .replace(/q/g, "k")
      .replace(/ck/g, "k")
      .replace(/ch/g, "sh")
      .replace(/c/g, "k")
      // Egyptian g is j (Gamila and Jamila), but gh stays gh.
      .replace(/gh/g, "G")
      .replace(/g/g, "j")
      .replace(/G/g, "gh")
      .replace(/(.)y/g, "$1i")
      .replace(/ee/g, "i")
      .replace(/(.)\1+/g, "$1")
      .replace(/h$/, "")
      .replace(/ou/g, "u")
      .replace(/o/g, "u")
      .replace(/e/g, "a")
      .replace(/(.)\1+/g, "$1")
  );
}

function bonesOf(key: string): string {
  return key.slice(0, 1) + key.slice(1).replace(/[aeiouy]/g, "");
}

function word(raw: string, arabic: boolean, pos: number, variant: boolean, span: 1 | 2 = 1): Word {
  const key = soundKey(raw);
  return { raw, key, bones: bonesOf(key), arabic, pos, span, variant };
}

const cache = new Map<string, Word[]>();

/** Splits the parts of one text made by `nameParts`. */
const PART_BREAK = " | ";

/**
 * One text made of parts, for a row found by its name and its level (or its coaches) together:
 * "Layla Beginners". Words join into pairs and "el" joins the next word only inside one part, so
 * "Taim Han" and "No level" never make the word "hanno".
 */
export function nameParts(...parts: readonly (string | null | undefined)[]): string {
  return parts.filter((part): part is string => !!part && part.trim().length > 0).join(PART_BREAK);
}

/** The words of one part of a name, "el" and "al" joined to the next word. */
function partWords(text: string): { arabic: boolean; raw: string }[] {
  const parts = text
    .split(/[\s\-_.,/()]+/)
    .map((piece) => ({ arabic: ARABIC.test(piece), raw: rawWord(piece) }))
    .filter((part) => part.raw.length > 0);
  const joined: { arabic: boolean; raw: string }[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    const next = parts[i + 1];
    if (ARTICLES.has(part.raw) && next) {
      parts[i + 1] = { arabic: part.arabic || next.arabic, raw: part.raw + next.raw };
      continue;
    }
    joined.push(part);
  }
  return joined;
}

/** The words of a name, with their second forms. Kept in a small cache: lists search the same names on every key. */
function wordsOf(name: string): Word[] {
  const hit = cache.get(name);
  if (hit) return hit;
  const words: Word[] = [];
  const variants: Word[] = [];
  let start = 0;
  for (const text of name.split(PART_BREAK)) {
    const joined = partWords(text);
    joined.forEach((part, at) => {
      const pos = start + at;
      words.push(word(part.raw, part.arabic, pos, false));
      const article = /^(el|al)(.{4,})$/.exec(part.raw);
      if (article) variants.push(word(article[2]!, part.arabic, pos, true));
      const next = joined[at + 1];
      if (next) variants.push(word(part.raw + next.raw, part.arabic, pos, true, 2));
    });
    start += joined.length;
  }
  words.push(...variants);
  if (cache.size > 5000) cache.clear();
  cache.set(name, words);
  return words;
}

/** Edit distance, swapped neighbours count as one, stopped above `max`. */
function distance(a: string, b: string, max: number, swaps: boolean): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const d: number[][] = [];
  for (let i = 0; i <= a.length; i++) d.push([i]);
  for (let j = 0; j <= b.length; j++) d[0]![j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const row = d[i]!;
      const up = d[i - 1]!;
      row[j] = Math.min(up[j]! + 1, row[j - 1]! + 1, up[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (swaps && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) row[j] = Math.min(row[j]!, d[i - 2]![j - 2]! + 1);
    }
  }
  return d[a.length]![b.length]!;
}

/** One typed word against one word of a name. */
function wordHit(typed: Word, have: Word, typing: boolean): NameHit | null {
  if (typed.raw === have.raw) return { score: 1, band: "exact", first: false };
  if (typing && typed.raw.length >= 2 && have.raw.startsWith(typed.raw)) return { score: 0.95, band: "exact", first: false };
  if (typed.key === have.key) return { score: 0.88, band: "sounds", first: false };
  if (typing && typed.key.length >= 3 && have.key.startsWith(typed.key)) return { score: 0.82, band: "sounds", first: false };
  // Arabic script on one side: it writes no short vowels, so only the consonants can be compared.
  if (typed.arabic || have.arabic) return typed.bones.length >= 2 && typed.bones === have.bones ? { score: 0.66, band: "arabic", first: false } : null;
  // Close spelling, with the first letter kept. Both 7+ letters: 2 letters. One of them 6+: 1 letter
  // (two swapped letters count as one). Both 4 or 5 letters: 1 vowel only, the consonants stay the
  // same, so Salma stays apart from Salem and Nayla from Naya.
  const min = Math.min(typed.key.length, have.key.length);
  const max = Math.max(typed.key.length, have.key.length);
  if (min >= 4 && typed.key[0] === have.key[0]) {
    if (max >= 6) {
      const allowed = min >= 7 ? 2 : 1;
      const edits = distance(typed.key, have.key, allowed, true);
      if (edits <= allowed) return { score: edits === 1 ? 0.72 : 0.6, band: "typo", first: false };
    } else if (typed.bones === have.bones && distance(typed.key, have.key, 1, false) <= 1) {
      return { score: 0.72, band: "typo", first: false };
    }
  }
  // A word typed with no vowels ("Omr", "Mhmd") is the consonants of the other word.
  if (typed.key.length >= 3 && have.key.length >= 3 && (typed.key === have.bones || have.key === typed.bones)) {
    return { score: 0.7, band: "typo", first: false };
  }
  return null;
}

/**
 * The typed text, ready to match. Null when nothing can be searched: no word of 2 letters or more
 * (one Arabic letter is enough). `typing` (the default) lets the last word match as a start.
 */
export function nameQuery(typed: string, options: { typing?: boolean } = {}): NameQuery | null {
  const words = wordsOf(typed.trim()).filter((w) => !w.variant && (w.raw.length >= 2 || w.arabic));
  return words.length > 0 ? { words, typing: options.typing ?? true } : null;
}

/** The query against one name: the hit, or null. Each typed word takes a different word of the name. */
export function matchName(query: NameQuery, name: string | null | undefined): NameHit | null {
  if (!name) return null;
  const have = wordsOf(name);
  const used = new Set<number>();
  let total = 0;
  let band = 0;
  for (let i = 0; i < query.words.length; i++) {
    const typed = query.words[i]!;
    const typing = query.typing && i === query.words.length - 1;
    let best: { hit: NameHit; word: Word } | null = null;
    for (const candidate of have) {
      if (used.has(candidate.pos) || (candidate.span === 2 && used.has(candidate.pos + 1))) continue;
      const hit = wordHit(typed, candidate, typing);
      if (hit && (!best || hit.score > best.hit.score)) best = { hit, word: candidate };
    }
    if (!best) return null;
    used.add(best.word.pos);
    if (best.word.span === 2) used.add(best.word.pos + 1);
    total += best.hit.score;
    band = Math.max(band, nameBands.indexOf(best.hit.band));
  }
  // A typed word that is the first name of the person ranks a little higher.
  return { score: total / query.words.length + (used.has(0) ? 0.03 : 0), band: nameBands[band]!, first: used.has(0) };
}

/** A text of a row: the row's own name, or another text that finds the row a little lower (a parent, a level). */
export type NameText = string | null | undefined | { text: string | null | undefined; via: true };

/** The best hit of the query over the texts of one row. */
export function matchTexts(query: NameQuery, texts: readonly NameText[]): NameHit | null {
  let best: NameHit | null = null;
  for (const entry of texts) {
    const via = typeof entry === "object" && entry !== null;
    const hit = matchName(query, via ? entry.text : entry);
    if (!hit) continue;
    const score = via ? hit.score * 0.9 : hit.score;
    if (!best || score > best.score) best = { ...hit, score };
  }
  return best;
}

export type NameResult<T> = { row: T; hit: NameHit };

/**
 * The rows that match the typed text, best first (same score: the order they came in). With
 * nothing to search (no word of 2 letters), every row in its own order, and `searched` is false.
 * `extra` lets a row in by another rule (a phone number), after the name hits; typed text with a
 * digit and no name is searched by `extra` alone.
 */
export function searchByName<T>(
  rows: readonly T[],
  typed: string,
  texts: (row: T) => readonly NameText[],
  options: { typing?: boolean; extra?: (row: T) => boolean } = {},
): { rows: T[]; results: NameResult<T>[]; searched: boolean } {
  const query = nameQuery(typed, options);
  const extra = options.extra;
  if (!query) {
    if (extra && /\p{Nd}/u.test(typed)) return { rows: rows.filter((row) => extra(row)), results: [], searched: true };
    return { rows: [...rows], results: [], searched: false };
  }
  const results: NameResult<T>[] = [];
  const others: T[] = [];
  rows.forEach((row) => {
    const hit = matchTexts(query, texts(row));
    if (hit) results.push({ row, hit });
    else if (extra?.(row)) others.push(row);
  });
  // Array.prototype.sort is stable: rows with the same score keep the order they came in.
  results.sort((a, b) => b.hit.score - a.hit.score);
  return { rows: [...results.map((result) => result.row), ...others], results, searched: true };
}

/** True when the typed text finds the row by one of its texts. A filter that keeps its own order uses this. */
export function nameMatches(typed: string, texts: readonly NameText[], options: { typing?: boolean } = {}): boolean {
  const query = nameQuery(typed, options);
  return query !== null && matchTexts(query, texts) !== null;
}

/** The best band of a search, or "none" when it found nothing. For analytics. */
export function topBand(results: readonly { hit: NameHit }[]): NameBand | "none" {
  if (results.length === 0) return "none";
  return nameBands[Math.min(...results.map((result) => nameBands.indexOf(result.hit.band)))]!;
}

/**
 * The search boxes that use this rule, for the `search_box` of `name_search_used`. One name for a
 * box on Desk web and on the phone: the tracker adds `surface` (desk_web or mobile) itself.
 */
export const nameSearchBoxes = [
  "home_participants",
  "families_parents",
  "families_participants",
  "members",
  "coach_members",
  "session_add",
  "new_participants",
  "new_participant_cards",
  "name_step_link",
  "whatsapp_sync",
  "coach_picker",
  "parent_wanted",
  "face_tag",
  "find_child",
  "someone_else",
] as const;
export type NameSearchBox = (typeof nameSearchBoxes)[number];

/**
 * What `name_search_used` sends for a search: counts and the best band, never the text. Null when
 * nothing was searched. `resultCount` counts rows let in by another rule too (a phone number).
 */
export function nameSearchEvent(
  box: NameSearchBox,
  typed: string,
  results: readonly { hit: NameHit }[],
  resultCount: number = results.length,
): { search_box: NameSearchBox; word_count: number; result_count: number; top_band: NameBand | "none" } | null {
  const query = nameQuery(typed);
  if (!query) return null;
  return { search_box: box, word_count: query.words.length, result_count: resultCount, top_band: topBand(results) };
}

export type NameSearchEvent = NonNullable<ReturnType<typeof nameSearchEvent>>;

/** How long the typed text must stay the same before the search counts as done. */
export const NAME_SEARCH_SETTLED_MS = 1500;

/**
 * The timing of `name_search_used`, the same on Desk web and on the phone: `send` runs when the
 * text stays the same for `NAME_SEARCH_SETTLED_MS`, once for each typed text (`sent` keeps every
 * text sent from the box, so "Layla", "Laila", "Layla" sends two events). Returns the cancel for an
 * effect's cleanup.
 */
export function settleNameSearchEvent(
  event: NameSearchEvent | null,
  typed: string,
  sent: Set<string>,
  send: (event: NameSearchEvent) => void,
): (() => void) | undefined {
  const text = typed.trim().toLowerCase();
  if (!event || sent.has(text)) return undefined;
  const timer = setTimeout(() => {
    sent.add(text);
    send(event);
  }, NAME_SEARCH_SETTLED_MS);
  return () => clearTimeout(timer);
}
